/**
 * BuilderRoofDetailsPanel — Iter167.
 *
 * One-line drop-in for every builder banner (Re-roof / Remedial Repairs /
 * Repairs / Painting / Restoration / Maintenance / Guttering) that
 * gives the operator a quick-reference + edit surface for the Roof
 * Details fields without leaving the builder.
 *
 * Self-contained: fetches the appraisal doc + its property's
 * roof_details on mount via the supplied api + appraisalId, then
 * delegates the heavy lifting to <RoofDetailsQuickBar> which already
 * handles preview mode, ⚙ Fields picker, ✎ Edit full modal, and
 * Apply / Discard.
 *
 * Props:
 *   - api: axios instance (required)
 *   - appraisalId: string (required)
 *   - onApplied?: () => void — fired after a successful Apply so the
 *     parent builder can re-fetch (rates, presets, etc.)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Home, Loader2, RefreshCw } from 'lucide-react';
import RoofDetailsQuickBar from '@/components/RoofDetailsQuickBar';
import { fireRoofDetailsChanged, onRoofDetailsChanged } from '@/utils/roofEvents';

const COLLAPSE_PREF_KEY = 'rtr.builder.roof_quickbar.collapsed.v1';

export default function BuilderRoofDetailsPanel({ api, appraisalId, onApplied }) {
  const [appraisal, setAppraisal] = useState(null);
  const [roofDetails, setRoofDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_PREF_KEY) === '1'; } catch (_) { return false; }
  });
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    if (!appraisalId) return;
    setLoading(true);
    try {
      // Iter169 — fetch the appraisal once and use its embedded
      // `roof_details` array directly. The /api/appraisals/{id}
      // endpoint joins the property's roof_details collection
      // (appraisals.py:663) so this is the same data the main
      // Roof Details / Conditions tab uses. Avoids a second fetch
      // + the race condition that left the panel empty when the
      // property collection diverged from the embedded array
      // shown elsewhere.
      const apRes = await api.get(`/appraisals/${appraisalId}`);
      const ap = apRes.data || {};
      setAppraisal(ap);
      let rds = Array.isArray(ap.roof_details) ? ap.roof_details : [];
      // Fallback for appraisals where the embed wasn't populated
      // (very old / unlinked records) — try the standalone endpoint.
      if (rds.length === 0 && ap.property_id) {
        const rdRes = await api.get(`/roof-details/${ap.property_id}`).catch(() => ({ data: [] }));
        if (Array.isArray(rdRes.data) && rdRes.data.length > 0) rds = rdRes.data;
      }
      setRoofDetails(rds);
    } finally {
      setLoading(false);
    }
  }, [api, appraisalId]);

  useEffect(() => { load(); }, [load]);

  // Iter168 — live-refresh when roof_details are added / updated /
  // deleted on the main Roof Details tab. The CRUD handlers in
  // AppraisalDetailPage + PropertyDetailPage call
  // fireRoofDetailsChanged() after each successful save.
  useEffect(() => onRoofDetailsChanged(() => { load(); }), [load]);

  const handleApplied = useCallback(async () => {
    await load();
    setPreview(null);
    // Tell every other panel (e.g. on Components tab) to re-fetch too
    fireRoofDetailsChanged();
    onApplied?.();
  }, [load, onApplied]);

  const toggle = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(COLLAPSE_PREF_KEY, next ? '1' : '0'); } catch (_) { /* ignore */ }
      return next;
    });
  };

  const effective = preview ? { ...(appraisal || {}), ...preview } : appraisal;

  return (
    <div
      className="border border-slate-200 rounded-md bg-white shadow-sm"
      data-testid="builder-roof-details-panel"
    >
      <div className="w-full flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100">
        <button
          type="button"
          onClick={toggle}
          data-testid="builder-roof-details-toggle"
          className="flex items-center gap-2 text-left hover:opacity-80"
          title="Quick-reference / edit Roof Details fields"
        >
          <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-slate-700">
            <Home size={13} className="text-cyan-700" />
            Roof Details — quick edit
            {loading && <Loader2 size={11} className="animate-spin text-slate-400" />}
            {roofDetails.length > 1 && (
              <span className="text-[10px] font-normal normal-case text-slate-400">
                ({roofDetails.length} roofs · pick one inside)
              </span>
            )}
            {/* Iter171 polish — keep the no-property warning visible
                even when the panel is collapsed so operators don't
                have to expand to discover why the data is missing. */}
            {!loading && appraisal && !appraisal.property_id && (
              <span className="text-[10px] font-bold normal-case text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-px">
                ⚠ No property linked
              </span>
            )}
          </span>
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); load(); }}
            data-testid="builder-roof-details-refresh"
            disabled={loading}
            className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-slate-500 hover:text-cyan-700 hover:bg-slate-100 disabled:opacity-40"
            title="Reload roof details from the server"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            type="button"
            onClick={toggle}
            aria-label={collapsed ? 'Expand' : 'Collapse'}
            className="text-slate-400 hover:text-slate-600 p-1"
          >
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
      </div>
      {!collapsed && (
        <div className="p-2">
          {loading ? (
            <div className="text-[11px] italic text-slate-400 px-2 py-1">Loading roof details…</div>
          ) : !appraisal?.property_id ? (
            /* Iter171 — surface the actual blocker. Without a
               property_id, no Roof Details can be saved at all so
               just pointing at the Roof Details tab is misleading. */
            <div className="text-[11px] px-2 py-1.5 text-amber-800 bg-amber-50 border border-amber-200 rounded">
              <strong>No property linked to this appraisal.</strong> Roof Details and Builder calcs need a property record. See the amber banner at the top of the appraisal page to find / create one.
            </div>
          ) : roofDetails.length === 0 ? (
            <div className="text-[11px] italic text-slate-500 px-2 py-1">
              No Roof Details on this appraisal yet. Add one on the <strong>Roof Details</strong> tab to enable live-edit here.
            </div>
          ) : (
            <RoofDetailsQuickBar
              api={api}
              appraisal={effective}
              roofDetails={roofDetails}
              onRoofPreview={setPreview}
              onApplied={handleApplied}
            />
          )}
        </div>
      )}
    </div>
  );
}
